import { styled } from '@linaria/react';
import {
  fonts,
  layout,
  primitives,
  radius,
  semantic,
  shadow,
  size,
  space,
} from '../../theme/tokens';

export const Panel = styled.div<{
  $columnView?: boolean;
  $singleMonth?: boolean;
  $stacked?: boolean;
}>`
  position: relative;
  background: ${semantic.bg.canvas};
  border-radius: ${radius('overlay', 'radius-md')};
  box-shadow: ${({ $stacked }) => ($stacked ? 'none' : shadow.lg)};
  padding: ${space['space-400']};
  box-sizing: border-box;
  width: ${({ $stacked }) => ($stacked ? '100%' : 'fit-content')};
  max-width: 100%;
  display: ${({ $stacked }) => ($stacked ? 'flex' : 'block')};
  flex-direction: column;
  flex: ${({ $stacked }) => ($stacked ? '1 1 auto' : '0 0 auto')};
  min-height: ${({ $stacked }) => ($stacked ? '0' : 'auto')};
  max-height: ${({ $stacked }) => ($stacked ? '100%' : 'none')};
`;

export const MonthsContainer = styled.div<{
  $columnView?: boolean;
  $singleMonth?: boolean;
  $stacked?: boolean;
  $pageScroll?: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${space['space-400']};
  overflow-y: ${({ $stacked, $pageScroll }) =>
    $pageScroll ? 'visible' : $stacked ? 'auto' : 'visible'};
  overscroll-behavior: ${({ $stacked, $pageScroll }) =>
    $pageScroll ? 'auto' : $stacked ? 'contain' : 'auto'};
  flex: ${({ $stacked }) => ($stacked ? '1 1 auto' : '0 0 auto')};
  min-height: ${({ $stacked }) => ($stacked ? '0' : 'auto')};
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 660px) {
    grid-template-columns: ${({ $columnView, $singleMonth, $stacked }) =>
      $stacked || $columnView || $singleMonth ? '1fr' : 'auto auto'};
    gap: ${space['space-600']};
  }

  @media (min-width: ${layout.desktopBreakpoint}) {
    overflow-y: ${({ $stacked }) => ($stacked ? 'auto' : 'visible')};
    overscroll-behavior: ${({ $stacked }) => ($stacked ? 'contain' : 'auto')};
  }
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${primitives.color.white['color-white-alpha-700']};
  border-radius: ${radius('overlay', 'radius-md')};
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  color: ${semantic.text.secondary};
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: ${space['space-400']};
  margin-top: ${space['space-400']};
`;

export const StackedWeekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${space['space-050']};
  padding-bottom: ${space['space-400']};
  margin-bottom: ${space['space-400']};
  border-bottom: ${size.stroke['stroke-default']} solid ${semantic.border.subtle};

  @media screen and (max-width: 600px) {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
`;

export const StackedWeekdayLabel = styled.span`
  text-align: center;
  font-family: ${fonts.body};
  font-size: ${fonts.size.sm};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.secondary};
  padding: ${space['space-100']} 0;
`;

export const LoadMoreButton = styled.button`
  align-self: center;
  background: ${semantic.bg.surfaceSubtle};
  border: none;
  border-radius: ${radius('button', 'radius-full')};
  padding: ${space['space-300']} ${space['space-600']};
  margin-top: ${space['space-600']};
  margin-bottom: ${space['space-400']};
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: ${fonts.size.md};
  font-weight: ${fonts.weight.semiBold};
  color: ${semantic.text.primary};
  transition: background 0.15s ease;

  &:hover {
    background: ${semantic.bg.surfaceDefault};
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const ResetButton = styled.button`
  background: none;
  border: none;
  padding: ${space['space-200']} 0;
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: ${fonts.size.md};
  font-weight: ${fonts.weight.medium};
  color: ${semantic.text.primary};

  &:hover {
    font-weight: ${fonts.weight.semiBold};
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }

  &:disabled {
    color: ${semantic.text.tertiary};
    cursor: not-allowed;
  }
`;

export const ApplyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${space['space-300']} ${space['space-600']};
  background: ${semantic.bg.brand};
  color: ${semantic.text.onBrand};
  border: none;
  border-radius: ${radius('button', 'radius-full')};
  cursor: pointer;
  font-family: ${fonts.body};
  font-size: ${fonts.size.md};
  font-weight: ${fonts.weight.semiBold};
  transition: opacity 0.15s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;
